import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { api } from '@/api/client';

interface PaymentModalProps {
  onClose: () => void;
  onSuccess?: () => void;
  context: { lat: number; lng: number };
}

export function PaymentModal({ onClose, onSuccess, context }: PaymentModalProps) {
  const [step, setStep] = useState<'choose' | 'qr' | 'success'>('choose');
  const [txId, setTxId] = useState<string | null>(null);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());

  async function startGuestPayment() {
    setLoading(true);
    setError(null);
    try {
      const res = await api<{ transactionId: string; referenceCode: string; qrUrl: string }>(
        '/api/payments/guest',
        { method: 'POST', body: JSON.stringify({ lat: context.lat, lng: context.lng }), skipAuth: true }
      );
      setTxId(res.transactionId);
      setQrUrl(res.qrUrl);
      setReference(res.referenceCode);
      setStep('qr');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Tạo giao dịch thất bại');
    } finally {
      setLoading(false);
    }
  }

  async function startSubscription(tier: 'basic' | 'pro') {
    setLoading(true);
    setError(null);
    try {
      const res = await api<{ transactionId: string; referenceCode: string; qrUrl: string }>(
        '/api/payments/subscribe',
        { method: 'POST', body: JSON.stringify({ tier }) }
      );
      setTxId(res.transactionId);
      setQrUrl(res.qrUrl);
      setReference(res.referenceCode);
      setStep('qr');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Tạo giao dịch thất bại');
    } finally {
      setLoading(false);
    }
  }

  // Poll for status when in QR step
  useEffect(() => {
    if (step !== 'qr' || !txId) return;
    const interval = setInterval(async () => {
      try {
        const res = await api<{ status: string }>(`/api/payments/${txId}/status`);
        if (res.status === 'confirmed') {
          setStep('success');
          onSuccess?.();
          clearInterval(interval);
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [step, txId, onSuccess]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {step === 'choose' && 'Xem chi tiết quy hoạch'}
            {step === 'qr' && 'Quét QR thanh toán'}
            {step === 'success' && '✅ Thành công'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>

        {error && <div className="bg-red-50 text-red-700 text-sm rounded p-2 mb-3">{error}</div>}

        {step === 'choose' && (
          <div className="space-y-3">
            {!isAuthenticated && (
              <button
                onClick={startGuestPayment}
                disabled={loading}
                className="w-full border-2 border-amber-300 bg-amber-50 rounded-lg p-4 text-left hover:bg-amber-100 disabled:opacity-50"
              >
                <p className="font-semibold text-amber-900">☕ Ủng hộ cốc cafe — 10.000đ</p>
                <p className="text-xs text-amber-700 mt-1">Xem chi tiết quy hoạch tại vị trí này (24h)</p>
              </button>
            )}

            {isAuthenticated ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-600">Hoặc mua gói thành viên:</p>
                <button
                  onClick={() => startSubscription('basic')}
                  disabled={loading}
                  className="w-full border-2 border-brand-300 rounded-lg p-4 text-left hover:bg-brand-50 disabled:opacity-50"
                >
                  <div className="flex justify-between items-center">
                    <p className="font-semibold text-gray-900">Gói Basic</p>
                    <p className="font-bold text-brand-600">50.000đ/tháng</p>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Xem chi tiết + đo đạc + so sánh</p>
                </button>
                <button
                  onClick={() => startSubscription('pro')}
                  disabled={loading}
                  className="w-full border-2 border-brand-500 bg-brand-50 rounded-lg p-4 text-left hover:bg-brand-100 disabled:opacity-50"
                >
                  <div className="flex justify-between items-center">
                    <p className="font-semibold text-gray-900">Gói Pro</p>
                    <p className="font-bold text-brand-700">150.000đ/tháng</p>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Basic + tải file + priority</p>
                </button>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
                💡 Mua gói tháng tiết kiệm hơn nếu tra cứu nhiều. <a href="/register" className="font-medium underline">Đăng ký</a>
              </div>
            )}
          </div>
        )}

        {step === 'qr' && qrUrl && (
          <div className="text-center">
            <img src={qrUrl} alt="QR thanh toán" className="mx-auto w-64 h-64 border border-gray-200 rounded" />
            <p className="text-sm text-gray-600 mt-3">
              Quét QR bằng app ngân hàng để chuyển khoản
            </p>
            {reference && (
              <p className="text-xs font-mono text-gray-500 mt-1 break-all">
                Nội dung: <span className="font-semibold">{reference}</span>
              </p>
            )}
            <div className="mt-3 flex items-center justify-center gap-2 text-sm text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600"></div>
              <span>Đang chờ thanh toán...</span>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              QR hết hạn sau 15 phút. Đóng cửa sổ nếu muốn hủy.
            </p>
          </div>
        )}

        {step === 'success' && (
          <div className="text-center">
            <div className="text-6xl mb-3">✅</div>
            <p className="text-lg font-semibold text-gray-900">Thanh toán thành công</p>
            <p className="text-sm text-gray-600 mt-2">
              Bạn có thể xem chi tiết quy hoạch ngay bây giờ.
            </p>
            <button
              onClick={onClose}
              className="mt-4 bg-brand-600 text-white px-6 py-2 rounded-md hover:bg-brand-700"
            >
              Đóng
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
